import * as THREE from "three";
import { batch, memoFn, mutate } from "@apendua/signal";
import { Polyhedron, Binding, getRandomPlaneThroughOrigin } from "@apendua.dev/polytope";
import { filter, Subject } from "rxjs";
import { ReactiveSet } from "@apendua/signal";
import PuzzleGroup from "./PuzzleGroup.js";
import { onCleanup, addDependency } from "@apendua/signal";

/**
 * Ordered so that neighbouring entries stay far enough apart in hue to be told
 * apart at a glance: a puzzle asks the player to match a partial shell against
 * the shell it came out of, and that only works if two facets are either
 * obviously the same colour or obviously not.
 */
const colorsPalette = [
  "#ff6b6b",
  "#ffd43b",
  "#4dabf7",
  "#a9e34b",
  "#f783ac",
  "#38d9a9",
  "#ffa94d",
  "#b197fc",
  "#63e6be",
  "#748ffc",
  "#ffe066",
  "#ff8787",
];

/**
 * Faces that only exist because the polytope was cut there. Holding them at a
 * grey with no colour in it at all is what makes a piece read as a shard of a
 * shell rather than as a solid of its own — and it has to stay light enough to
 * catch the light, because how a cut face is shaded is most of what tells the
 * player which way round the piece goes.
 */
const CUT_FACE_COLOR = "#6d7787";

/**
 * The inset band drawn around every face. It sits in the plane of the face, so
 * this is not a highlight but an outline: dark, and the same everywhere, which
 * is what keeps a hundred facets from dissolving into one smear of colour.
 */
const EDGE_COLOR = "#161b22";

export interface PuzzleOptions {
  nPieces: number;
  nColors: number;
  allowRotations: boolean;
  allowTranslations: boolean;
  maxRotationAngle: number;
  maxTranslationDistance: number;
  maxMergeDistance: number;
  addFullPolyhedronPiece: boolean;
}

function matrixDistance(A: THREE.Matrix4, B: THREE.Matrix4) {
  const identity = new THREE.Matrix4();
  const relative = new THREE.Matrix4().copy(A).invert().multiply(B);
  const a = identity.elements;
  const b = relative.elements;
  const distance = Math.max(...a.map((v, i) => Math.abs(v - b[i])));
  return distance;
  // let sum = 0;
  // for (let i = 0; i < 16; i++) {
  //   sum += (a[i] - b[i]) ** 2;
  // }
  // return Math.sqrt(sum);
}

/**
 * How many places to consider before throwing a piece at one of them. More
 * candidates means a tidier spread; the cost is a handful of vector
 * subtractions per piece, once per deal.
 */
const SCATTER_CANDIDATES = 32;

/**
 * Picks somewhere to throw the next piece: best-candidate sampling, which
 * draws a few uniform positions and keeps whichever one lands furthest from
 * everything already dealt.
 *
 * Drawing one position and using it — which is what this used to do — puts two
 * shards inside one another often enough to matter, and a pair that share a
 * volume cannot be told apart at all until one of them is dragged out. This
 * always terminates and always returns the most open spot it saw, so a puzzle
 * cut into more pieces than there is room for still gets dealt; it just gets
 * dealt tightly.
 *
 * @param taken Centres of the pieces already placed.
 * @param offset Where this piece sits relative to the position being chosen.
 * @param d Half-width of the cube pieces are scattered through.
 */
function scatter(taken: THREE.Vector3[], offset: THREE.Vector3, d: number): THREE.Vector3 {
  const best = new THREE.Vector3();
  let bestClearance = -Infinity;
  const candidate = new THREE.Vector3();
  const center = new THREE.Vector3();
  for (let i = 0; i < SCATTER_CANDIDATES; i++) {
    candidate.set(2 * Math.random() * d - d, 2 * Math.random() * d - d, 2 * Math.random() * d - d);
    center.copy(candidate).add(offset);
    let clearance = Infinity;
    for (const other of taken) {
      clearance = Math.min(clearance, center.distanceTo(other));
    }
    if (clearance > bestClearance) {
      bestClearance = clearance;
      best.copy(candidate);
    }
  }
  return best;
}

interface PuzzleReset {
  type: "reset";
}

type PuzzleEvent = PuzzleReset;

export default class Puzzle {
  #polyhedron: Polyhedron;
  #pieces: Map<string, Polyhedron>;
  #groups: ReactiveSet<PuzzleGroup>;
  #options: PuzzleOptions;
  #events = new Subject<PuzzleEvent>();
  #anyResetEvent = this.#events.pipe(filter((event) => event.type === "reset"));

  constructor(polyhedron: Polyhedron, options?: Partial<PuzzleOptions>) {
    this.#polyhedron = polyhedron.clone();
    this.#pieces = new Map<string, Polyhedron>();
    this.#groups = new ReactiveSet<PuzzleGroup>();
    this.#options = {
      nPieces: 4,
      nColors: 1,
      allowRotations: true,
      allowTranslations: true,
      maxRotationAngle: Math.PI / 4,
      maxTranslationDistance: 3,
      maxMergeDistance: 0.2,
      addFullPolyhedronPiece: false,
    };
    this.init(options);
  }

  reset() {
    this.init();
  }

  init(options?: Partial<PuzzleOptions>) {
    this.#options = {
      ...this.#options,
      ...options,
    };
    const polyhedron = this.#polyhedron.clone();
    for (const edge of polyhedron.edges()) {
      edge.color = EDGE_COLOR;
    }
    for (const face of polyhedron.faces()) {
      face.color =
        colorsPalette[Math.floor(Math.random() * this.#options.nColors) % colorsPalette.length];
    }
    let pieces: Polyhedron[] = [polyhedron];
    let iterations = 0;
    // Dive each piece into half until we have enough pieces.
    while (pieces.length < this.#options.nPieces && iterations < 1000) {
      const newPieces: Polyhedron[] = [];
      let i = 0;
      while (
        iterations < 1000 &&
        i < pieces.length &&
        newPieces.length + pieces.length - i < this.#options.nPieces
      ) {
        const plane = getRandomPlaneThroughOrigin(pieces[i].getCenter());
        const piece1 = pieces[i].clone();
        const piece2 = pieces[i].clone();
        const face1 = piece1.intersectWithPlane(
          plane[0],
          plane[1],
          plane[2],
          plane[3],
          CUT_FACE_COLOR,
        );
        const face2 = piece2.intersectWithPlane(
          -plane[0],
          -plane[1],
          -plane[2],
          -plane[3],
          CUT_FACE_COLOR,
        );
        if (face1 && face2) {
          let minEdgeLength = Infinity;
          for (const vertex of face1.vertices()) {
            for (const edge of piece1.getAdjecentEdges(vertex.id)) {
              if (edge.length < minEdgeLength) {
                minEdgeLength = edge.length;
              }
            }
          }
          for (const vertex of face2.vertices()) {
            for (const edge of piece2.getAdjecentEdges(vertex.id)) {
              if (edge.length < minEdgeLength) {
                minEdgeLength = edge.length;
              }
            }
          }
          if (minEdgeLength > 0.1) {
            newPieces.push(piece1, piece2);
            i++;
          }
        }
        iterations++;
      }
      pieces = [...pieces.slice(i), ...newPieces];
    }
    if (this.#options.addFullPolyhedronPiece) {
      pieces.unshift(this.#polyhedron.clone());
    }
    this.#pieces = new Map();
    for (let i = 0; i < pieces.length; i++) {
      // Splitting an edge carries its colour over, but the edges that bound a
      // brand new cut face have none, and an uncoloured edge is drawn white.
      // Left alone that reads as a bright seam exactly where the eye is
      // supposed to see an ordinary outline.
      for (const edge of pieces[i].edges()) {
        if (!edge.color) {
          edge.color = EDGE_COLOR;
        }
      }
      this.#pieces.set(i.toString(), pieces[i]);
    }
    batch(() => {
      this.#groups.clear();
      // Where the pieces already dealt ended up, so the next one can be thrown
      // somewhere they are not.
      const taken: THREE.Vector3[] = [];
      for (const [key, piece] of this.#pieces) {
        const group = new PuzzleGroup(this);
        group.pieces.add(key);
        // randomize position and rotation
        if (!this.#options.addFullPolyhedronPiece || key !== "0") {
          if (this.#options.allowRotations) {
            const a = this.#options.maxRotationAngle;
            group.object.rotation.set(
              2 * Math.random() * a - a,
              2 * Math.random() * a - a,
              2 * Math.random() * a - a,
            );
          }
          if (this.#options.allowTranslations) {
            // A piece sits off to one side of the group's origin — that origin
            // is the centre of the sphere it was cut out of, not of the shard —
            // so it is the shard's own centre, after the turn it was just
            // given, that has to be kept clear of the others.
            const offset = piece.getCenter().applyEuler(group.object.rotation);
            const position = scatter(taken, offset, this.#options.maxTranslationDistance);
            group.object.position.copy(position);
            taken.push(position.clone().add(offset));
          }
        }
        this.#groups.add(group);
      }
    });
    this.#events.next({ type: "reset" });
  }

  get groups() {
    return this.#groups;
  }

  /**
   * How many pieces the polytope is currently cut into. Not the same as the
   * `nPieces` that was asked for: the cut gives up after a bounded number of
   * attempts, so what came out is what the panel has to report.
   */
  get nPieces() {
    addDependency(this.#anyResetEvent);
    return this.#pieces.size;
  }

  /** How many separate clumps are still floating around, solved being one. */
  get nGroups() {
    return this.#groups.size;
  }

  get solved() {
    return this.#groups.size <= 1;
  }

  @mutate()
  drop(group: PuzzleGroup) {
    for (const g of this.#groups.values()) {
      if (g !== group) {
        g.object.updateMatrixWorld();
        group.object.updateMatrixWorld();
        if (
          matrixDistance(g.object.matrixWorld, group.object.matrixWorld) <
          this.#options.maxMergeDistance
        ) {
          for (const key of group.pieces.values()) {
            g.pieces.add(key);
          }
          this.#groups.delete(group);
          return true;
        }
      }
    }
    return false;
  }

  getPiece(key: string): Polyhedron {
    const piece = this.#pieces.get(key);
    if (!piece) {
      throw new Error("Piece not found");
    }
    return piece;
  }

  @memoFn()
  getGeometry(key: string, bevel: boolean): THREE.BufferGeometry {
    addDependency(this.#anyResetEvent);
    let piece: Polyhedron | undefined;
    if (!key) {
      piece = this.#polyhedron;
    } else {
      piece = this.#pieces.get(key);
    }
    if (!piece) {
      throw new Error("Piece not found");
    }
    const geometry = bevel
      ? piece.getBevelGeometry({
          edgeWidth: 0.02,
        })
      : piece.getFaceGeometry({
          edgeWidth: 0.02,
        });
    const vertexBindings: Binding[] = geometry.userData.vertexBindings;
    const color: number[] = [];
    for (let i = 0; i < vertexBindings.length; i++) {
      const { type, id } = vertexBindings[i];
      if (type === "edge") {
        const edge = piece.getEdge(id);
        if (edge.color) {
          color.push(...new THREE.Color(edge.color).toArray());
          continue;
        }
      }
      if (type === "face") {
        const face = piece.getFace(id);
        if (face.color) {
          color.push(...new THREE.Color(face.color).toArray());
          continue;
        }
      }
      color.push(1.0, 1.0, 1.0);
    }
    geometry.setAttribute("color", new THREE.Float32BufferAttribute(color, 3));
    onCleanup(() => {
      geometry.dispose();
    });
    return geometry;
  }
}
